// CreditNova Auth Provider
import React, { createContext, useContext, useCallback, useEffect, useState } from 'react';
import {
  MsalProvider,
  useMsal,
  useIsAuthenticated,
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
} from '@azure/msal-react';
import { InteractionRequiredAuthError, AccountInfo } from '@azure/msal-browser';
import { msalInstance, loginRequest, apiRequest } from '@/lib/auth/msal-config';
import { setTokenProvider } from '@/lib/api-client';

export interface AuthContextValue {
  isAuthenticated: boolean;
  isLoading: boolean;
  isReady: boolean; // True when auth is complete and token provider is set
  user: AccountInfo | null;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  getAccessToken: () => Promise<string>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

function AuthProviderInner({ children }: { children: (auth: AuthContextValue) => React.ReactNode }) {
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [isLoading, setIsLoading] = useState(true);
  const [isReady, setIsReady] = useState(false);

  const user = accounts[0] || null;

  // Get access token
  const getAccessToken = useCallback(async (): Promise<string> => {
    if (accounts.length === 0) {
      throw new Error('No authenticated user');
    }

    console.log('Acquiring token with scopes:', apiRequest.scopes);
    
    try {
      const response = await instance.acquireTokenSilent({
        ...apiRequest,
        account: accounts[0],
        forceRefresh: false,
      });
      
      // Check if we got an access token
      if (!response.accessToken) {
        console.error('No access token in response, falling back to popup');
        throw new InteractionRequiredAuthError('no_access_token');
      }
      
      console.log('Token acquired successfully');
      console.log('Token type:', response.tokenType);
      console.log('Scopes granted:', response.scopes);
      
      // Log token details for debugging
      try {
        const [, payload] = response.accessToken.split('.');
        const decoded = JSON.parse(atob(payload));
        console.log('Token audience (aud):', decoded.aud);
        console.log('Token issuer (iss):', decoded.iss);
      } catch (e) {
        console.warn('Could not decode token for logging');
      }
      
      return response.accessToken;
    } catch (error: any) {
      console.error('Silent token acquisition failed:', error?.message || error);
      
      // Always try popup on any failure
      console.log('Falling back to popup for token acquisition...');
      try {
        const response = await instance.acquireTokenPopup({
          ...apiRequest,
          account: accounts[0],
        });
        console.log('Popup token acquired, scopes:', response.scopes);
        return response.accessToken;
      } catch (popupError) {
        console.error('Popup token acquisition also failed:', popupError);
        throw popupError;
      }
    }
  }, [instance, accounts]);

  // Login
  const login = useCallback(async () => {
    try {
      await instance.loginRedirect(loginRequest);
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  }, [instance]);

  // Logout
  const logout = useCallback(async () => {
    try {
      await instance.logoutRedirect({
        postLogoutRedirectUri: window.location.origin,
      });
    } catch (error) {
      console.error('Logout failed:', error);
      throw error;
    }
  }, [instance]);

  // Set up token provider for API client
  useEffect(() => {
    if (isAuthenticated) {
      setTokenProvider(getAccessToken);
      setIsReady(true);
      console.log('Auth ready: token provider set');
    } else {
      setIsReady(false);
    }
  }, [isAuthenticated, getAccessToken]);

  // Handle redirect callback
  useEffect(() => {
    const handleRedirect = async () => {
      try {
        await instance.handleRedirectPromise();
      } catch (error) {
        console.error('Redirect handling failed:', error);
      } finally {
        setIsLoading(false);
      }
    };

    handleRedirect();
  }, [instance]);

  // Update loading state based on interaction
  useEffect(() => {
    if (inProgress === 'none') {
      setIsLoading(false);
    }
  }, [inProgress]);

  const authValue: AuthContextValue = {
    isAuthenticated,
    isLoading,
    isReady,
    user,
    login,
    logout,
    getAccessToken,
  };

  return (
    <AuthContext.Provider value={authValue}>
      {children(authValue)}
    </AuthContext.Provider>
  );
}

export function AuthProvider({ children }: { children: (auth: AuthContextValue) => React.ReactNode }) {
  return (
    <MsalProvider instance={msalInstance}>
      <AuthProviderInner>{children}</AuthProviderInner>
    </MsalProvider>
  );
}

// Convenience components
export { AuthenticatedTemplate, UnauthenticatedTemplate };

// HOC for protected routes
export function withAuth<P extends object>(
  Component: React.ComponentType<P>
): React.FC<P> {
  return function AuthenticatedComponent(props: P) {
    const { isAuthenticated, isLoading, login } = useAuth();

    if (isLoading) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-accent-500" />
        </div>
      );
    }

    if (!isAuthenticated) {
      return (
        <div className="flex flex-col items-center justify-center min-h-screen gap-4">
          <h1 className="text-2xl font-semibold">Authentication Required</h1>
          <p className="text-muted-foreground">Please sign in to continue</p>
          <button
            onClick={login}
            className="px-6 py-3 bg-accent-500 text-white rounded-lg hover:bg-accent-600 transition-colors"
          >
            Sign in with Microsoft
          </button>
        </div>
      );
    }

    return <Component {...props} />;
  };
}

